import { PhoneNumber as PhoneNumberType } from "./models";
import { Combobox, ComboboxOption } from "@headlessui/react";
import { Input } from "cy-fe";
import { PhoneCountryCode, phoneCountryCodes } from "./phoneCountryCodes";
import { cn } from "cy-fe/src/utils/cn";
import { ComboBox, ComboOption, createFilterOptionsFn } from "../Combobox";
import { comboBoxButtonContent } from "./comboBoxButtonContent";
import { FocusEventHandler } from "react";

export function PhoneNumberInput({
  inputId,
  value,
  onChange,
  error,
  disabled,
  loading,
  onNumberBlur,
  placeholder,
  onCountryChange,
  onNumberChange,
  required,
  "data-testid": dataTestId,
}: {
  inputId?: string;
  required?: boolean;
  value: PhoneNumberType;
  disabled?: boolean;
  onChange?: (value: PhoneNumberType) => void;
  onCountryChange?: (value: PhoneCountryCode) => void;
  onNumberChange?: (value: string) => void;
  error?: boolean;
  loading?: boolean;
  onNumberBlur?: FocusEventHandler<HTMLInputElement>;
  placeholder?: string;
  "data-testid"?: string;
}) {
  const selectedCountryCode = phoneCountryCodes.find(
    (x: PhoneCountryCode) => x.id === value.isd,
  );
  const baseTestId = dataTestId || "phone-number-input";

  function handleCountryChange(country: PhoneCountryCode) {
    onCountryChange?.(country);
    if (onChange != null) {
      onChange({ isd: country.id, number: value.number });
    }
  }

  function handleNumberChange(number: string) {
    onNumberChange?.(number);
    if (onChange != null) {
      onChange({ isd: value.isd, number });
    }
  }
  return (
    <div data-testid={baseTestId} className="flex items-center gap-5">
      <ComboBox<PhoneCountryCode>
        label="phone country code"
        data-testid={`${baseTestId}-country-code`}
        buttonClassName="max-w-[5rem]"
        allowedPlacements={["top-start", "bottom-start"]}
        ignoreCalculatedWidth
        error={error}
        onChange={(country) => {
          if (country) {
            handleCountryChange(country);
          }
        }}
        panelClassName="sm:w-[17rem]"
        value={selectedCountryCode || null}
        getOptions={(query) =>
          createFilterOptionsFn<PhoneCountryCode>(
            (x) => `${x.prefix} ${x.cca3} ${x.name}`,
          )(query, phoneCountryCodes)
        }
        loading={loading}
        disabled={disabled}
        buttonChildren={(val) => comboBoxButtonContent("lg", val && { ...val })}
        renderItems={(opts) =>
          opts.map((option) => (
            <ComboboxOption key={option.prefix} value={option}>
              {({ focus, selected }) => (
                <ComboOption active={focus} selected={selected}>
                  <div className="flex items-center">
                    <div className="ml-2">
                      <span
                        className={cn(
                          "truncate text-base font-semibold",
                          !option.prefix && "hidden",
                        )}
                      >
                        {option.prefix}{" "}
                      </span>
                      <span
                        className={cn("text-sm", !!option.prefix && "pl-2")}
                      >
                        {option.cca3}{" "}
                      </span>
                    </div>
                  </div>
                </ComboOption>
              )}
            </ComboboxOption>
          ))
        }
      />
      <Input
        data-testid={`${baseTestId}-number`}
        id={inputId || "phone-number-input"}
        aria-label="phone number"
        vSize="lg"
        autoComplete={"tel-national"}
        name={"tel-number"}
        disabled={disabled || loading}
        onBlur={onNumberBlur}
        required={required}
        vColor={error ? "error" : undefined}
        onChange={(e) => handleNumberChange(e.target.value)}
        placeholder={placeholder || ""}
        value={value.number || ""}
      />
    </div>
  );
}
